import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../api/axios";

export default function Account() {
	const { user, loading, isQRSession, logout } = useAuth();
	const navigate = useNavigate();
	const [orders, setOrders] = useState([]);
	const [loadingOrders, setLoadingOrders] = useState(true);
	
	useEffect(() => {
		if (!loading && !user) navigate("/login");
		if (!loading && isQRSession) navigate("/order");
	}, [user, loading, isQRSession, navigate]);
		
		useEffect(() => {
			if (!user) return;
			api
			.get("/orders/my")
			.then(({ data }) => setOrders(data))
			.catch(console.error)
			.finally(() => setLoadingOrders(false));
		}, [user]);
		
		if (loading) return null;
		if (!user) return null;
		
		return (
			<div className="space-y-6">
			
			{/* ── Account summary ── */}
			<div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6
			border border-farm-tan/30 dark:border-gray-700
			transition-colors duration-200">
			<h2 className="text-2xl font-bold text-farm-green dark:text-green-400
			mb-4">
			My Account
			</h2>
			
			<div className="grid grid-cols-2 gap-4 mb-4">
			<InfoCard
			label="Name"
			value={`${user.firstName} ${user.lastName}`}
			/>
			<InfoCard label="Email" value={user.email} />
			<InfoCard
			label="Account #"
			value={user.accountNumber}
			mono
			/>
			<InfoCard
			label="Balance"
			value={`${user.balance?.toFixed(2)} credits`}
			highlight
			/>
			</div>
			
			{user.lastOrderAt && (
				<p className="text-sm text-gray-500 dark:text-gray-400">
				Last order:{" "}
				{new Date(user.lastOrderAt).toLocaleDateString("en-US", {
					month: "long",
					day: "numeric",
					year: "numeric",
				})}
				</p>
			)}
			
			<div className="flex gap-3 mt-4">
			<Link
			to="/order"
			className="flex-1 bg-farm-green text-white py-2 rounded-xl
			font-semibold text-center hover:bg-green-700
			transition-colors"
			>
			Order Eggs
			</Link>
			<Link
			to="/add-credits"
			className="flex-1 border-2 border-farm-green text-farm-green
			dark:border-green-400 dark:text-green-400 py-2
			rounded-xl font-semibold text-center
			hover:bg-farm-green hover:text-white
			dark:hover:bg-green-700 dark:hover:text-white
			transition-colors"
			>
			Add Credits
			</Link>
			</div>
			</div>
			
			{/* ── QR code info ── */}
			<div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6
			border border-farm-tan/30 dark:border-gray-700
			transition-colors duration-200">
			<h3 className="text-lg font-bold text-farm-green dark:text-green-400
			mb-2">
			Your Quick-Order QR Code
			</h3>
			<p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
			Share your personal QR link for fast ordering. Anyone who scans
			it will still need your PIN to place an order.
			</p>
			<div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3
			font-mono text-xs break-all text-gray-700
			dark:text-gray-300 border border-gray-200
			dark:border-gray-600">
			{`${window.location.origin}/qr-order?account=${user.accountNumber}`}
			</div>
			<p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
			Paste the URL above into a free QR generator such as{" "}
			<a
			href="https://qr.io"
			target="_blank"
			rel="noreferrer"
			className="text-farm-green dark:text-green-400 hover:underline"
			>
			qr.io
			</a>
			.
			</p>
			</div>
			
			{/* ── 2FA status ── */}
			<TotpStatusPanel totpEnabled={user.totpEnabled} />
			
			{/* ── Change password ── */}
			<ChangePasswordPanel />
			
			{/* ── Change PIN ── */}
			<ChangePinPanel />
			
			{/* ── Order history ── */}
			<div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6
			border border-farm-tan/30 dark:border-gray-700
			transition-colors duration-200">
			<h3 className="text-lg font-bold text-farm-green dark:text-green-400
			mb-4">
			Recent Orders
			</h3>
			
			{loadingOrders ? (
				<div className="text-center text-gray-400 dark:text-gray-500 py-4">
				Loading…
				</div>
			) : orders.length === 0 ? (
				<p className="text-gray-500 dark:text-gray-400 text-sm">
				No orders yet.
				</p>
			) : (
				<div className="space-y-3">
				{orders.map((order) => (
					<div
					key={order.id}
					className="border border-gray-100 dark:border-gray-700
					rounded-xl p-4 bg-gray-50 dark:bg-gray-700/50
					transition-colors duration-200"
					>
					<div className="flex justify-between items-start">
					<div>
					<div className="font-semibold text-gray-800
					dark:text-gray-100">
					🥚 {order.eggCount} Eggs
					</div>
					<div className="text-xs text-gray-500 dark:text-gray-400
					mt-0.5">
					Pickup:{" "}
					{new Date(order.desiredDate).toLocaleDateString(
						"en-US",
						{
							month: "short",
							day: "numeric",
							year: "numeric",
						}
					)}
					</div>
					{order.discount > 0 && (
						<div className="text-xs text-green-600
						dark:text-green-400 mt-0.5">
						🎟️ Coupon saved {order.discount.toFixed(2)}{" "}
						credits — charged {order.finalCost.toFixed(2)}
						</div>
					)}
					{order.notes && (
						<div className="text-xs text-gray-400
						dark:text-gray-500 mt-0.5 italic">
						"{order.notes}"
						</div>
					)}
					</div>
					<div className="text-xs text-gray-400 dark:text-gray-500">
					{new Date(order.createdAt).toLocaleDateString()}
					</div>
					</div>
					</div>
				))}
				</div>
			)}
			</div>
			
			{/* ── Sign out ── */}
			<button
			onClick={() => {
				logout();
				navigate("/");
			}}
			className="w-full border-2 border-red-300 dark:border-red-700
			text-red-600 dark:text-red-400 py-2 rounded-xl
			hover:bg-red-50 dark:hover:bg-red-900/20
			transition-colors font-medium"
			>
			Sign Out
			</button>
			</div>
		);
}

// ── 2FA status panel ──────────────────────────────────────────────────────
function TotpStatusPanel({ totpEnabled }) {
	const navigate = useNavigate();
	
	return (
		<div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6
		border border-farm-tan/30 dark:border-gray-700
		transition-colors duration-200">
		<div className="flex items-center justify-between">
		<div className="flex items-center gap-3">
		<span className="text-2xl">🔐</span>
		<div>
		<h3 className="font-bold text-farm-green dark:text-green-400">
		Two-Factor Authentication
		</h3>
		<p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
		{totpEnabled
			? "Your account is protected with TOTP-based 2FA."
			: "2FA is not yet configured on your account."}
			</p>
			</div>
			</div>
			
			{totpEnabled ? (
				<span className="bg-green-100 dark:bg-green-900/40 text-green-700
				dark:text-green-400 text-xs font-semibold px-3
				py-1 rounded-full">
				✓ Enabled
				</span>
			) : (
				<button
				onClick={() => navigate("/totp-setup")}
				className="bg-farm-green text-white text-sm px-4 py-1.5
				rounded-xl hover:bg-green-700 transition-colors
				font-semibold"
				>
				Set Up 2FA
				</button>
			)}
			</div>
			</div>
	);
}

// ── Change password panel ─────────────────────────────────────────────────
function ChangePasswordPanel() {
	const [open, setOpen] = useState(false);
	const [form, setForm] = useState({
		currentPassword: "",
		newPassword: "",
		confirmPassword: "",
	});
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [success, setSuccess] = useState("");
	
	const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
	
	const handleSubmit = async (e) => {
		e.preventDefault();
		setError("");
		setSuccess("");
		
		if (form.newPassword !== form.confirmPassword) {
			return setError("New passwords do not match.");
		}
		if (form.newPassword.length < 8) {
			return setError("New password must be at least 8 characters.");
		}
		if (form.currentPassword === form.newPassword) {
			return setError(
				"New password must be different from your current password."
			);
		}
		
		setLoading(true);
		try {
			await api.post("/auth/change-password", {
				currentPassword: form.currentPassword,
				newPassword: form.newPassword,
			});
			setSuccess("Password changed successfully!");
			setForm({
				currentPassword: "",
				newPassword: "",
				confirmPassword: "",
			});
			setTimeout(() => {
				setSuccess("");
				setOpen(false);
			}, 3000);
		} catch (err) {
			setError(
				err.response?.data?.error ||
				"Failed to change password. Please try again."
			);
		} finally {
			setLoading(false);
		}
	};
	
	return (
		<CollapsiblePanel
		icon="🔑"
		title="Change Password"
		open={open}
		onToggle={() => {
			setOpen(!open);
			setError("");
			setSuccess("");
		}}
		>
		{error && <AlertBox type="error" message={error} />}
		{success && <AlertBox type="success" message={`✅ ${success}`} />}
		
		<form onSubmit={handleSubmit} className="space-y-4 mt-4">
		{[
			{
				key: "currentPassword",
				label: "Current Password",
				placeholder: "Enter current password",
			},
			{
				key: "newPassword",
				label: "New Password",
				placeholder: "At least 8 characters",
			},
			{
				key: "confirmPassword",
				label: "Confirm New Password",
				placeholder: "Re-enter new password",
			},
		].map(({ key, label, placeholder }) => (
			<PanelInput
			key={key}
			type="password"
			label={label}
			value={form[key]}
			onChange={set(key)}
			placeholder={placeholder}
			/>
		))}
		
		<button
		type="submit"
		disabled={loading}
		className="w-full bg-farm-green text-white py-2.5 rounded-xl
		font-semibold hover:bg-green-700 transition-colors
		disabled:opacity-60"
		>
		{loading ? "Updating…" : "Update Password"}
		</button>
		</form>
		</CollapsiblePanel>
	);
}

// ── Change PIN panel ──────────────────────────────────────────────────────
function ChangePinPanel() {
	const [open, setOpen] = useState(false);
	const [form, setForm] = useState({
		password: "",
		newPin: "",
		confirmPin: "",
	});
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [success, setSuccess] = useState("");
	
	const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
	
	const handleSubmit = async (e) => {
		e.preventDefault();
		setError("");
		setSuccess("");
		
		if (form.newPin !== form.confirmPin) {
			return setError("PINs do not match.");
		}
		if (!/^\d{4,8}$/.test(form.newPin)) {
			return setError("PIN must be 4–8 digits (numbers only).");
		}
		
		setLoading(true);
		try {
			await api.post("/auth/change-pin", {
				password: form.password,
				newPin: form.newPin,
				confirmPin: form.confirmPin,
			});
			setSuccess("PIN changed successfully!");
			setForm({ password: "", newPin: "", confirmPin: "" });
			setTimeout(() => {
				setSuccess("");
				setOpen(false);
			}, 3000);
		} catch (err) {
			setError(
				err.response?.data?.error ||
				"Failed to change PIN. Please try again."
			);
		} finally {
			setLoading(false);
		}
	};
	
	return (
		<CollapsiblePanel
		icon="🔢"
		title="Change Quick-Order PIN"
		open={open}
		onToggle={() => {
			setOpen(!open);
			setError("");
			setSuccess("");
		}}
		>
		<p className="text-sm text-gray-500 dark:text-gray-400 mt-3">
		Your PIN is used to authenticate quick orders via your personal
		QR code. Enter your account password to authorize the change.
		</p>
		
		{error && <AlertBox type="error" message={error} />}
		{success && <AlertBox type="success" message={`✅ ${success}`} />}
		
		<form onSubmit={handleSubmit} className="space-y-4 mt-4">
		<PanelInput
		type="password"
		label="Account Password"
		value={form.password}
		onChange={set("password")}
		placeholder="Enter your password to confirm"
		/>
		
		<div className="grid grid-cols-2 gap-3">
		<PanelInput
		type="password"
		label="New PIN (4–8 digits)"
		value={form.newPin}
		onChange={set("newPin")}
		placeholder="1234"
		maxLength={8}
		inputMode="numeric"
		/>
		<PanelInput
		type="password"
		label="Confirm New PIN"
		value={form.confirmPin}
		onChange={set("confirmPin")}
		placeholder="1234"
		maxLength={8}
		inputMode="numeric"
		/>
		</div>
		
		<button
		type="submit"
		disabled={loading}
		className="w-full bg-farm-green text-white py-2.5 rounded-xl
		font-semibold hover:bg-green-700 transition-colors
		disabled:opacity-60"
		>
		{loading ? "Updating…" : "Update PIN"}
		</button>
		</form>
		</CollapsiblePanel>
	);
}

// ── Shared sub-components ─────────────────────────────────────────────────
function CollapsiblePanel({ icon, title, open, onToggle, children }) {
	return (
		<div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border
		border-farm-tan/30 dark:border-gray-700 overflow-hidden
		transition-colors duration-200">
		<button
		type="button"
		onClick={onToggle}
		className="w-full flex items-center justify-between p-6
		hover:bg-gray-50 dark:hover:bg-gray-700/50
		transition-colors"
		>
		<div className="flex items-center gap-3">
		<span className="text-xl">{icon}</span>
		<span className="font-bold text-farm-green dark:text-green-400
		text-lg">
		{title}
		</span>
		</div>
		<svg
		className={`h-5 w-5 text-gray-400 dark:text-gray-500
			transition-transform ${open ? "rotate-180" : ""}`}
			fill="none"
			stroke="currentColor"
			viewBox="0 0 24 24"
			>
			<path
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
			d="M19 9l-7 7-7-7"
			/>
			</svg>
			</button>
			
			{open && (
				<div className="px-6 pb-6 border-t border-gray-100
				dark:border-gray-700">
				{children}
				</div>
			)}
			</div>
	);
}

function PanelInput({
	type,
	label,
	value,
	onChange,
	placeholder,
	maxLength,
	inputMode,
}) {
	return (
		<div>
		<label className="block text-sm font-medium text-gray-700
		dark:text-gray-300 mb-1">
		{label}
		</label>
		<input
		type={type}
		required
		value={value}
		onChange={onChange}
		placeholder={placeholder}
		maxLength={maxLength}
		inputMode={inputMode}
		className="w-full border border-gray-300 dark:border-gray-600
		rounded-lg px-3 py-2 bg-white dark:bg-gray-700
		text-gray-800 dark:text-gray-100
		placeholder-gray-400 dark:placeholder-gray-500
		focus:outline-none focus:ring-2 focus:ring-farm-green
		transition-colors"
		/>
		</div>
	);
}

function AlertBox({ type, message }) {
	const styles =
	type === "error"
	? "bg-red-50 dark:bg-red-900/30 border-red-300 dark:border-red-600 text-red-700 dark:text-red-300"
	: "bg-green-50 dark:bg-green-900/30 border-green-300 dark:border-green-600 text-green-700 dark:text-green-300";
	
	return (
		<div className={`border rounded-lg p-3 mt-3 text-sm ${styles}`}>
		{message}
		</div>
	);
}

function InfoCard({ label, value, mono, highlight }) {
	return (
		<div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-3 border
		border-gray-100 dark:border-gray-600
		transition-colors duration-200">
		<div className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">
		{label}
		</div>
		<div
		className={`
			font-semibold text-sm break-all
			${highlight
				? "text-farm-green dark:text-green-400"
				: "text-gray-800 dark:text-gray-100"}
				${mono ? "font-mono" : ""}
				`}
				>
				{value}
				</div>
				</div>
	);
}
